import React, { useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Button, TextField, Box } from '@material-ui/core';
import * as Icon from '../Icon';
import { MessageProps } from '@chatscope/chat-ui-kit-react';
import { Texts } from '../../constants';
import useStyles from './styles';
import MessageList from '../MessageList';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { WidgetProps } from '../../containers/Widget/types';

interface WidgetDialogProps {
  isOpened: boolean
  messages: MessageProps[]
  isLoading: boolean
  onMessageSend: (message: string, contextAction?: string) => void
  onMessageRegenerate: (index: number) => void
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void
  onNewChat: (event: React.MouseEvent<HTMLButtonElement>) => void
  dialogPosition?: DOMRect
  isContextActionsAvailable?: boolean
  widgetProps: WidgetProps
}

const WidgetDialog: React.FC<WidgetDialogProps> = ({
  isOpened,
  messages,
  isLoading,
  onMessageSend,
  onMessageRegenerate,
  onClose,
  onNewChat,
  dialogPosition,
  isContextActionsAvailable,
  widgetProps,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles({ widgetPosition: dialogPosition ?? {} });
  const [isChatInputAvailable, setIsChatInputAvailable] = useState(!isContextActionsAvailable);
  const { userName, dummyPrompts } = widgetProps;
  const [isExpandView, setExpandView] = React.useState(false);

  const handleSendMessage = () => {
    if (!inputRef.current?.value) {
      return;
    }

    onMessageSend(inputRef.current.value)

    inputRef.current.value = ''
  }

  const handleSummarizeAction = () => {
    setIsChatInputAvailable(true);
    onMessageSend('Please, summarize the document', 'summarization')
  }

  const handleChatAction = () => {
    setIsChatInputAvailable(true);
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSendMessage();
    }
  }

  return (
    <Dialog
      disableEnforceFocus
      disableScrollLock
      hideBackdrop
      classes={{
        root: classes.root,
      }}
      PaperProps={{ className: `${classes.paper} ${isExpandView ? 'expanded' : ''}` }}
      scroll="paper"
      open={isOpened}
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item className={classes.titleLeftContainer}>
            <div>
              <Icon.Stars className={classes.marginRight} />
              <span>{Texts.dialogTitle}</span>
            </div>
            <IconButton
              className={classes.newButton}
              size="small"
              onClick={onNewChat}
              data-cy="new-chat"
            >
              <Icon.Plus className={classes.marginRight} />
              <span>{Texts.New}</span>
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton
              className={classes.resizeButton}
              disableFocusRipple
              onClick={() => setExpandView((prev) => !prev)}
              data-cy={isExpandView ? "compress-size" : "expand-size" }
            >
              {isExpandView ? <Icon.compressSolid /> : <Icon.expandSolid />}
            </IconButton>
            <IconButton
              size="medium"
              className={classes.closeButton}
              disableFocusRipple
              onClick={onClose}
              data-cy="close-chat"
            >
              <Icon.XMark />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {!messages.length ? (
          <>
          <Box className={classes.dummyPromptsContainer}>
            {dummyPrompts && dummyPrompts?.length > 0 && <span className={`${classes.helloText} ${classes.promptHeader}`} data-cy="commonly-used-prompts">{Texts.usedPrompts}</span> }
            {dummyPrompts?.map((prompt, i) => {
              return <span className={classes.dummyPrompts} data-cy={`example-prompt-${i}`}>{prompt}</span>
            })}
          </Box>
          <div className={classes.welcomeTextContainerTM} data-cy="hello-user-screen">
            <span className={classes.helloText}>Hello, {userName}</span>
            <span className={classes.helpText}>{Texts.helpText}</span>
          </div>
          </>
        ) :
        <MessageList
          widgetProps={widgetProps}
          messages={messages}
          isLoading={isLoading}
          onMessageRegenerate={onMessageRegenerate}
        />}
      </DialogContent>
      {isContextActionsAvailable && !isChatInputAvailable && (
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleSummarizeAction}
            color="primary"
            variant="contained"
          >
            Summarize attachments
          </Button>
          <Button
            onClick={handleChatAction}
            color="primary"
            variant="contained"
          >
            Chat
          </Button>
        </DialogActions>
      )}
      {isChatInputAvailable && (
        <DialogActions className={classes.dialogActions}>
          <TextField
            inputRef={inputRef}
            placeholder={Texts.enterYourPrompt}
            variant="outlined"
            className={classes.inputField}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            data-cy="message-input"
          />
          <IconButton
            className={classes.sendButton}
            onClick={handleSendMessage}
            disabled={isLoading}
            data-cy="send-message"
          >
            <Icon.PaperPlane />
          </IconButton>
        </DialogActions>
      )}
      <span className={classes.importantInfo}>{Texts.importantInfo}</span>
    </Dialog>
  );
};

export default WidgetDialog;
